import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import { AnyRecord } from '../../utils';

import { PROMPT_ROOT } from './PromptContainer';
import { selectPortalProps } from './selectors';
import { prompt } from './slice';
import { PromptProviderComponentProps, PromptProviderProps } from './PromptProvider.types';
import { useCustomProps, registerType } from './usePrompt';

function PromptProvider<Params extends AnyRecord = Record<never, never>>({
    type,
    children,
    component: Component,
    ...promptComponentProps
}: PromptProviderProps<Params>) {
    useEffect(() => {
        if (type !== undefined) {
            registerType(type);
        }
    }, [type]);

    const dispatch = useDispatch();

    const { ready, currentType, params } = useSelector(selectPortalProps);

    if (!ready || !type || type !== currentType) {
        return null;
    }

    const MOUNT_NODE = document.getElementById(PROMPT_ROOT);

    if (!MOUNT_NODE) {
        return null;
    }

    const passDownProps: PromptProviderComponentProps<Params> = {
        onClose: () => dispatch(prompt.failure()),
        submit: () => dispatch(prompt.success()),
        useCustomProps,
        ...promptComponentProps,
        ...(params as Params),
    };

    let element = null as null | React.ReactNode;

    if (Component) {
        element = <Component {...passDownProps} />;
    } else if (typeof children === 'function') {
        element = children(passDownProps);
    } else {
        element = children;
    }

    return createPortal(element, MOUNT_NODE);
}

export default React.memo(PromptProvider) as typeof PromptProvider;
